import moment from 'moment';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { petNameSyndeticJoin } from '@rover/react-lib/src/utils/pets';
import type { OptimizedSearchResult } from '@rover/rsdk/src/apiClient/latest/model/optimizedSearchResult';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';

import buildBestMatchesHeader from './buildBestMatchesHeader';
import buildMainSearchResultsHeader from './buildMainSearchResultsHeader';
import buildSelectedProviderHeader from './buildSelectedProviderHeader';
import { HeaderScenariosEnum } from './constants';
import { DateData, PrebuiltHeaderProps } from './types';
import { getFormattedPetData } from './utils';

export type BuilderProps = {
  dates?: Record<'startDate' | 'endDate', Date | undefined>;
  hasBestMatches?: boolean;
  hasResults?: boolean;
  bookingFeePercentage: string;
  frequency?: ServiceFrequencyEnum;
  isAuthenticated?: boolean;
  isBestMatchExperience?: boolean;
  isBestMatchesSearch?: boolean;
  isInitialPage?: boolean;
  isSeoPage?: boolean;
  language: string;
  petData: {
    dogCount?: number;
    catCount?: number;
    puppyCount?: number;
    petCount?: number;
  };
  priceTransparencyLevel: PriceTransparencyLevel;
  selectedProvider?: OptimizedSearchResult;
  serviceType: ServiceType;
  spacesRequired?: number;
  isInSimplifiedMobileFilters?: boolean;
};

type HeaderDataPayloadOptions<H> = H extends typeof HeaderScenariosEnum.SEARCH_RESULTS
  ? PrebuiltHeaderProps | null
  : // Right now we don't expect the builder to handle no-results or low-supply headers
  H extends typeof HeaderScenariosEnum.NO_RESULTS | typeof HeaderScenariosEnum.LOW_SUPPLY
  ? never
  : PrebuiltHeaderProps;

type HeaderDataPayload = {
  [H in HeaderScenariosEnum]?: HeaderDataPayloadOptions<H>;
};

/**
 * Given a start and end date, returns dates formatted for display in the header
 * as well as whether or not they're the same date.
 */
const getFormattedDates = (
  language: string,
  startDate: Date | undefined,
  endDate: Date | undefined
): DateData => {
  const DF = getDateTimeFormatMapForLang(language);
  // If we are missing any dates, we'll prompt the user to add them.
  if (!startDate || !endDate) {
    return { isSameDate: false };
  }

  moment.locale(language);

  const sameDate = startDate?.getTime() === endDate?.getTime();
  const sameYear = startDate?.getFullYear() === endDate?.getFullYear();
  const format = sameYear ? DF.MONTH_DAY_MED : DF.DATE_MED;

  return {
    isSameDate: sameDate,
    startDate: moment(startDate).format(format),
    endDate: moment(endDate).format(format),
  };
};

const getFormattedStartDate = (language: string, startDate: Date | undefined): DateData => {
  const DF = getDateTimeFormatMapForLang(language);

  moment.locale(language);

  return {
    isSameDate: false,
    startDate: startDate ? moment(startDate).format(DF.DATE_MED) : undefined,
  };
};

const searchHeaderBuilder = ({
  dates,
  hasBestMatches = false,
  hasResults,
  bookingFeePercentage,
  frequency,
  isAuthenticated = false,
  isBestMatchExperience,
  isBestMatchesSearch,
  isInitialPage,
  isSeoPage = false,
  language,
  petData,
  priceTransparencyLevel,
  selectedProvider,
  serviceType,
  // spacesRequired is used by unauthenticated mobile web users instead of pet counts
  spacesRequired = 0,
}: BuilderProps): HeaderDataPayload => {
  const headers = {};
  const { startDate, endDate } = dates || {};

  const isRepeat = frequency === ServiceFrequencyEnum.REPEAT;
  const processedDates = isRepeat
    ? getFormattedStartDate(language, startDate)
    : getFormattedDates(language, startDate, endDate);
  const hasPets = Object.values(petData).some((count) => count > 0) || spacesRequired > 0;

  const processedPetData = getFormattedPetData(petData);
  const individualPetTypeCountDescription = petNameSyndeticJoin(processedPetData);

  const { dogCount = 0, catCount = 0, puppyCount = 0, petCount: paramPetCount = 0 } = petData;

  const petCount = paramPetCount || dogCount + catCount + puppyCount;

  if (selectedProvider) {
    headers[HeaderScenariosEnum.SELECTED_SITTER] = buildSelectedProviderHeader({
      dateData: processedDates,
      bookingFeePercentage,
      individualPetTypeCountDescription,
      isAuthenticated,
      isBestMatchesSearch,
      priceTransparencyLevel,
      selectedProvider,
      serviceType,
      petCount,
    });
  }

  if (isBestMatchesSearch && hasBestMatches) {
    headers[HeaderScenariosEnum.BEST_MATCHES] = buildBestMatchesHeader({
      dateData: processedDates,
      bookingFeePercentage,
      hasBestMatches,
      individualPetTypeCountDescription,
      isAuthenticated,
      priceTransparencyLevel,
      serviceType,
      petCount,
    });
  }

  if (hasResults) {
    headers[HeaderScenariosEnum.SEARCH_RESULTS] = buildMainSearchResultsHeader({
      bookingFeePercentage,
      hasBestMatches,
      isAuthenticated,
      isBestMatchExperience,
      isBestMatchesSearch,
      isInitialPage,
      isRepeat,
      priceTransparencyLevel,
      individualPetTypeCountDescription,
      selectedProvider,
      serviceType,
      dateData: processedDates,
      hasPets,
      isSeoPage,
      petCount,
    });
  }

  return headers;
};

export default searchHeaderBuilder;
